import React, { useState } from 'react';
import { track } from '@amplitude/analytics-browser';
import { Alert, Box, Button, Icon, Text } from '@nimbus-ds/components';
import { CalculatorIcon } from '@nimbus-ds/icons';
import { useIsMobile, useStoreInfo } from 'commons/hooks';
import {
  PaymentMethodsType,
  PaymentProviderComparisonResult,
} from 'commons/types';
import { useTranslationWithPrefix } from 'commons/utils';
import CalculatedResultsCard from './CalculatedResultsCard';

interface CalculatedResultsContainerProps {
  results: PaymentProviderComparisonResult[];
  paymentMethodType: PaymentMethodsType;
  onCompareAgain: () => void;
  onActivatePagoNube: () => void;
  installmentsQty: number;
}

const CalculatedResultsContainer: React.FC<CalculatedResultsContainerProps> = ({
  results,
  paymentMethodType,
  onCompareAgain,
  onActivatePagoNube,
  installmentsQty,
}) => {
  const { t } = useTranslationWithPrefix('landing.feeCalculatorModal');

  const [expandedGateways, setExpandedGateways] = useState<Set<string>>(
    new Set(),
  );

  const { storeInfo } = useStoreInfo();
  const isMobile = useIsMobile();

  if (!results?.length) {
    return (
      <Box marginTop="4">
        <Alert appearance="danger">{t('results.noResults')}</Alert>
      </Box>
    );
  }

  const toggleDetails = (gateway: string) => {
    setExpandedGateways((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(gateway)) {
        newSet.delete(gateway);
      } else {
        newSet.add(gateway);
      }
      return newSet;
    });
    track('tiendanube_mx_pagonube_landing_calculator_details_click', {
      payment_method_displayed: gateway,
    });
  };

  const compareAgainButton = (
    <Button appearance="neutral" onClick={onCompareAgain} fullWidth={isMobile}>
      <Icon source={<CalculatorIcon size="small" />} />
      {t('results.compareAgainButton')}
    </Button>
  );

  const activateButton = (
    <Button
      appearance="primary"
      onClick={onActivatePagoNube}
      fullWidth={isMobile}
    >
      <Box minWidth={isMobile ? undefined : '165px'}>
        {t('results.activatePagoNubeButton')}
      </Box>
    </Button>
  );

  return (
    <Box display="flex" flexDirection="column" gap="4">
      <Box padding="3" backgroundColor="neutral-surface" borderRadius="2">
        <Text fontSize="base" color="neutral-textHigh">
          {t('results.paymentMethodTitle')}:{' '}
          <span style={{ fontWeight: 'bold' }}>
            {t(`paymentMethodOptions.${paymentMethodType}`)}
          </span>
        </Text>
      </Box>

      <Box display="flex" flexDirection="column" gap="3">
        {results.map((result: PaymentProviderComparisonResult) => {
          const competitorTransactionCost =
            results.find((r) => r.gateway !== result.gateway)
              ?.transactionCostAmount ?? 0;

          return (
            <CalculatedResultsCard
              key={result.gateway}
              result={result}
              storeInfo={storeInfo}
              isExpanded={expandedGateways.has(result.gateway)}
              onToggleDetails={() => toggleDetails(result.gateway)}
              savingsAmount={competitorTransactionCost}
              installmentsQty={installmentsQty}
            />
          );
        })}
      </Box>

      <Box
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        gap="2"
      >
        {/* DOM order matches visual order so keyboard focus follows it */}
        {isMobile ? (
          <>
            {activateButton}
            {compareAgainButton}
          </>
        ) : (
          <>
            {compareAgainButton}
            {activateButton}
          </>
        )}
      </Box>
    </Box>
  );
};

export default CalculatedResultsContainer;
